/**
 * アップロード済みの動画素材から、シナリオなしで動画エディタの初期プロジェクトを組み立てる
 * (docs/features/video-editor-presentation-import-plan.md §3.2 / §4.1)。
 *
 * FE が作るのは**作成時の初期データだけ**: 素材 1 本の sourceClip と、素材全長を参照する timeline 項目 1 件。
 * 文字起こし・発話ベースの分割・テロップは取り込みジョブ (BE) が同じ project に書き足すので、
 * ここでは解析結果から何も作らない (二重生成を防ぐ)。timeline を空にしないのは、BE の自動分割
 * (_auto_split_timeline_scenes) が timeline が空だと何もしないため。
 */
import { roundSec, validateProject, type EditProject, type OutputSettings, type SourceClip, type TimelineItem } from '../schema/project';
import { newInstanceId, userLineage } from '../schema/lineage';
import { formatDefaultProjectName } from '../projectName';

/** 入力: 素材行 (CloudMaterial / TemporaryMaterial) のうち組み立てに要る列だけ。 */
export interface MaterialForEditProject {
  id: string;
  /** true = 個人フォルダ (temporary_materials) / false = 共有フォルダ (materials)。 */
  isTemporary: boolean;
  content_type?: string | null;
  /** 素材の実尺 (秒)。ffprobe のメタデータジョブが書く。無い素材からは作れない。 */
  duration_seconds?: number | null;
  width?: number | null;
  height?: number | null;
  name?: string | null;
}

/** 出力 fps。シナリオ起点 (buildEditProjectFromScenario の FPS) と同じ 24。 */
const FPS = 24;

/**
 * 素材の実寸から出力サイズを決める。横長 = 16:9 1080p、縦長 = 9:16、正方形 = 1080x1080。
 * 実寸が無い素材は横長とみなす (プレゼンの画面収録は横長が大半)。
 */
export function resolveOutputForMaterial(material: Pick<MaterialForEditProject, 'width' | 'height'>): OutputSettings {
  const w = material.width ?? 0;
  const h = material.height ?? 0;
  if (w > 0 && h > 0) {
    if (w === h) return { width: 1080, height: 1080, fps: FPS };
    if (h > w) return { width: 1080, height: 1920, fps: FPS };
  }
  return { width: 1920, height: 1080, fps: FPS };
}

export function buildEditProjectFromMaterial(
  material: MaterialForEditProject,
  opts?: { name?: string },
): EditProject {
  const durationSec = roundSec(material.duration_seconds ?? 0);
  if (!(durationSec > 0)) {
    throw new Error('動画の長さが取得できていない素材からは編集を作れません');
  }
  const output = resolveOutputForMaterial(material);
  const hasSize = !!material.width && !!material.height && material.width > 0 && material.height > 0;

  // シナリオ起点の動画クリップ (buildVisualClip) と同じ形。width/height/fps は出力と同値 (既存規約)、
  // 実寸は srcWidth/srcHeight に入れる (クロップ計算の基準)。sourceSceneId は付けない (シーン由来ではない)。
  const clip: SourceClip = {
    id: crypto.randomUUID(),
    type: 'video',
    materialId: material.id,
    isTemporary: material.isTemporary,
    durationSec,
    width: output.width,
    height: output.height,
    fps: output.fps,
    ...(hasSize ? { srcWidth: material.width!, srcHeight: material.height! } : {}),
    label: material.name?.trim() || null,
    scriptLines: [],
  };

  // 素材全長 1 件。取り込みジョブが発話ベースの区切りに作り直す (計画 §3.2)。
  const item: TimelineItem = {
    id: newInstanceId(),
    clipId: clip.id,
    inSec: 0,
    outSec: durationSec,
    transition: { type: 'cut', durationSec: 0 },
    captions: [],
    excluded: false,
    lineage: userLineage(),
  };

  const now = new Date().toISOString();
  const project = {
    id: crypto.randomUUID(),
    name: opts?.name ?? formatDefaultProjectName(),
    createdAt: now,
    updatedAt: now,
    prompt: '',
    sourceClips: [clip],
    timeline: [item],
    bgmTrack: [],
    bgmCandidates: [],
    seTrack: [],
    seCandidates: [],
    narrationTrack: [],
    output,
    // シナリオ由来ではないので sourceSnapshot は持たない (シナリオ更新の検知対象外)
    history: [
      {
        timestamp: now,
        actor: 'user' as const,
        action: 'create-from-material',
        detail: { materialId: material.id, isTemporary: material.isTemporary },
      },
    ],
  };

  return validateProject(project);
}
